import React, { useState, useMemo } from "react";
import { Select, Box, TextField, InputAdornment, Button } from "@mui/material";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import CustomCheckbox from "./Form/CustomCheckbox";
import { COLORS } from "../themes/Base";
import ReplayIcon from "@mui/icons-material/Replay";
import useButtonStyles from "../styles/Buttons";
import useSharedStyles from "../styles/Shared";

type Country = { code: string; name: string };
type Props = {
  value: string[];
  onChange: (codes: string[]) => void;
  options: Country[];
  supplierCount?: (code: string) => number;
  label?: string;
};

const CountryFilterDropdown: React.FC<Props> = ({
  value,
  onChange,
  options,
  supplierCount,
  label = "All Countries",
}) => {
  const [search, setSearch] = useState("");
  const { classes: buttonClasses, cx } = useButtonStyles();
  const { classes } = useSharedStyles();

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter(
      (country) =>
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.code.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleClearAll = () => onChange([]);

  return (
    <Select
      multiple
      displayEmpty
      value={value}
      renderValue={() =>
        value.length === 0
          ? label
          : `${value.length} country${value.length > 1 ? "ies" : "y"} selected`
      }
      IconComponent={(props) =>
        props.className?.includes("MuiSelect-iconOpen") ? (
          <ChevronUpIcon sx={{ color: COLORS.iconColor }} />
        ) : (
          <ChevronDownIcon sx={{ color: COLORS.iconColor }} />
        )
      }
      MenuProps={{
        PaperProps: {
          className: classes.innerBox,
          sx: { maxHeight: 300, width: 260, fontSize: 14 },
        },
      }}
    >
      <Box sx={{ pb: 1 }}>
        <TextField
          sx={{
            p: 0,
            borderBottom: 0,
            "& .MuiInputBase-root::before": {
              borderBottom: 0,
            },
          }}
          fullWidth
          size="small"
          placeholder="Search countries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ color: "#67676C", width: "20px", ml: 1 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {filteredOptions.length === 0 ? (
        <Box sx={{ px: 2, py: 1, textAlign: "center", fontSize: 14 }}>
          No countries available to filter.
        </Box>
      ) : (
        <Box>
          {filteredOptions.map((country) => (
            <Box key={country.code} sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <CustomCheckbox
                sx={{ py: 0 }}
                checked={value.indexOf(country.code) > -1}
                onChange={() => {
                  if (value.includes(country.code)) {
                    onChange(value.filter((c) => c !== country.code));
                  } else {
                    onChange([...value, country.code]);
                  }
                }}
              />
              <Box component="span" sx={{ fontSize: 16 }}>
                {country.code} - {country.name}
              </Box>
              {supplierCount && (
                <Box
                  sx={{
                    ml: "auto",
                    mr: 1,
                    px: 2,
                    border: "1px solid " + COLORS.inputBorder,
                    borderRadius: "12px",
                  }}
                >
                  {supplierCount(country.code)}
                </Box>
              )}
            </Box>
          ))}
          <Box sx={{ mx: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              className={cx(buttonClasses.iconButton, buttonClasses.iconButton)}
              onClick={handleClearAll}
              fullWidth
            >
              Clear All
            </Button>
          </Box>
        </Box>
      )}
    </Select>
  );
};

export default CountryFilterDropdown;
